"use client";

import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Flame, ShieldCheck, Zap, Clock } from 'lucide-react';
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const processSteps = [{
  id: "01",
  title: "Initial Consultation",
  description: "Our journey begins with a gentle conversation. We discuss your fears, hopes, and expectations to ensure a deeply personalized support plan.",
  image: "https://sunny-rooster.10web.cloud/wp-content/uploads/2026/05/Nicole.avif?v=6y3invpcsxp",
  icon: Clock
}, {
  id: "02",
  title: "Thoughtful Planning",
  description: "Together, we craft a flexible roadmap for your delivery. From comfort measures to medical preferences, every detail is considered with care.",
  image: "https://sunny-rooster.10web.cloud/wp-content/uploads/2026/05/Tezza-3941.avif?v=ylbu2wyg12q",
  icon: ShieldCheck
}, {
  id: "03",
  title: "Steady Labor Support",
  description: "When the time arrives, I am by your side. Providing physical comfort, emotional grounding, and advocacy through every wave of labor.",
  image: "https://sunny-rooster.10web.cloud/wp-content/uploads/2026/05/Tezza-0425-scaled.jpg?v=urxmv1edaz",
  icon: Flame
}, {
  id: "04",
  title: "Nurturing Care",
  description: "The support continues at home. We focus on your recovery, newborn bonding, and lactation success during those precious first weeks.",
  image: "https://sunny-rooster.10web.cloud/wp-content/uploads/2026/05/Tezza-3372.avif?v=78mpif50lt7",
  icon: Zap
}];
function ProcessStepItem({
  step,
  index
}: {
  step: typeof processSteps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.4
  });
  const isEven = index % 2 === 0;
  const Icon = step.icon;
  return <div data-section-id="223933" data-index={index} ref={ref} className={cn("flex flex-col gap-8 lg:gap-16 items-center relative z-10", isEven ? "lg:flex-row" : "lg:flex-row-reverse")}>
            {}
            <div className="flex-1 w-full flex flex-col sm:flex-row items-start gap-6 lg:gap-8">
                {}
                <div className="relative shrink-0 select-none">
                    <div className="text-7xl sm:text-8xl lg:text-[10rem] font-black font-default leading-none tracking-tighter">
                        {}
                        <span className="text-transparent" style={{
            WebkitTextStroke: '1px var(--color-border)'
          }}>
                            {step.id}
                        </span>
                        {}
                        <span className="absolute left-0 top-0 transition-all duration-1000 ease-out !font-normal !text-[rgba(237,185,185,1)] !font-serif !text-[100px] ![padding:0px_10px_0px_10px]" style={{
            clipPath: isVisible ? 'inset(0 0 0 0)' : 'inset(100% 0 0 0)'
          }}>
                            {step.id}
                        </span>
                    </div>
                </div>

                {}
                <div className={cn("pt-2 sm:pt-6 lg:pt-10 transition-all duration-700 delay-300", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-primary/10 rounded-full text-primary">
                            <Icon className="w-5 h-5" />
                        </div>
                        <h3 className="text-2xl !font-normal !text-[rgba(237,185,185,1)] !font-serif sm:text-3xl lg:text-4xl">
                            {step.title}
                        </h3>
                    </div>
                    <p className="text-lg leading-relaxed max-w-md font-default !text-[rgba(242,242,242,1)]">
                        {step.description}
                    </p>
                </div>
            </div>

            {}
            <div className="flex-1 w-full">
                <div className={cn("relative aspect-[4/3] sm:aspect-video lg:aspect-[4/3] rounded-3xl overflow-hidden shadow-xl transition-all duration-1000 delay-100", isVisible ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-12")}>
                    <img src={step.image} alt={step.title} className="w-full h-full object-cover transition-transform duration-1000 hover:scale-105" />
                    {}
                    <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent pointer-events-none" />
                    <div className="absolute inset-0 ring-1 ring-inset ring-white/5 rounded-3xl pointer-events-none" />
                </div>
            </div>
        </div>;
}
export default function ProcessSteps() {
  return <section id="the-path-to-peace" className="dark py-24 overflow-hidden relative !bg-[rgba(123,116,52,1)] !text-[rgba(50,51,30,1)] lg:py-32">
            {}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[500px] bg-primary/5 rounded-full blur-[120px] pointer-events-none opacity-40" />
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                
                {}
                <div className="text-center max-w-3xl mx-auto mb-20 lg:mb-32">
                    <h2 className="font-semibold tracking-widest uppercase mb-4 text-xs !text-[rgba(50,51,30,1)] !font-sans sm:text-sm">Your Doula Journey</h2>
                    <h3 className="text-4xl mb-6 !font-light !text-[rgba(50,51,30,1)] !font-serif sm:text-5xl lg:text-6xl">
                        The Path to <span className="!text-[rgba(50,51,30,1)] !italic">Peace</span>
                    </h3>
                    <p className="text-lg sm:text-xl font-default text-primary">
                        A gentle, structured approach to your birthing experience. From our first meeting to your transition into parenthood, I am with you every step of the way.
                    </p>
                </div>

                {}
                <div className="relative flex flex-col gap-24 lg:gap-40">
                    {}
                    <div className="hidden absolute left-1/2 top-16 bottom-16 w-px bg-transparent border-l border-border/30 -translate-x-1/2 z-0 !border-solid border-[oklab(0.3735410.01098670.0172849/0.3)] !border-2 lg:block text-primary" />

                    {processSteps.map((step, index) => <ProcessStepItem key={step.id} step={step} index={index} />)}
                </div>

                {}
                <div className="mt-24 lg:mt-40 text-center relative z-10">
                    <div className="inline-flex flex-col items-center p-8 rounded-4xl border border-border/20 shadow-3xl relative overflow-hidden !bg-[rgba(50,51,30,1)]">
                        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent opacity-30 !text-[rgba(50,51,30,1)]"></div>
                        <h4 className="text-2xl font-bold mb-6 relative z-10 !text-[rgba(237,185,185,1)] !font-serif sm:text-3xl lg:text-4xl">Ready to begin your journey?</h4>
                        <p className="mb-10 max-w-md relative z-10 font-default text-lg text-primary">
                            Take the first step toward a peaceful and supported birth experience. I would love to hear your story.
                        </p>
                        <Button asChild size="lg" className="inline-flex items-center justify-center gap-2 whitespace-nowrap [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive h-10 has-[>svg]:px-4 rounded-full px-10 py-7 text-lg font-bold relative z-10 group text-primary-foreground shadow-lg transition-all duration-300 !bg-[rgba(237,185,185,1)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90">
                            <Link to="https://hello.dubsado.com/public/appointment-scheduler/662a79049b3568003af78fe0/schedule">
                                Book Your Consultation
                                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                            </Link>
                        </Button>
                    </div>
                </div>

            </div>
        </section>;
}